import Cookies from 'js-cookie';
import { SecurityService } from './securityService';

const TOKEN_COOKIE_NAME = 'quiz_token';
const TOKEN_EXPIRY_DAYS = 7;

// Nouvelle fonction de validation de token plus robuste utilisant SecurityService
export function validateToken(token: any): token is string {
  return SecurityService.validateQuizToken(token);
}

// Fonction pour détecter la navigation privée (approximative mais utile)
const isPrivateBrowsing = (): boolean => {
  try {
    // Test basique : essayer de stocker et récupérer une valeur dans localStorage
    localStorage.setItem('__test__', '1');
    localStorage.removeItem('__test__');
    return false; // Si on arrive ici, localStorage fonctionne
  } catch (e) {
    return true; // Si on a une erreur, on est probablement en navigation privée
  }
};

export const tokenService = {
  /**
   * Enregistre le token dans un cookie et localStorage comme fallback
   */
  setToken(token: string): void {
    try {
      // Essayer d'abord avec un cookie - Configuration sécurisée
      Cookies.set(TOKEN_COOKIE_NAME, token, {
        expires: TOKEN_EXPIRY_DAYS,
        sameSite: 'strict', // Plus sécurisé que 'lax', protège contre les attaques CSRF
        secure: true, // Force l'utilisation de HTTPS
        // Note: 'httpOnly' ne peut pas être défini côté client et nécessiterait un backend
        path: '/' // Assure que le cookie est accessible sur tout le site
      });
      
      // Stocker également dans localStorage comme fallback
      try {
        localStorage.setItem(TOKEN_COOKIE_NAME, token);
      } catch (e) {
        console.warn('Impossible de stocker le token dans localStorage', e);
      }
    } catch (e) {
      console.warn('Impossible de stocker le token dans les cookies', e);
    }
  },

  /**
   * Récupère le token depuis le cookie ou localStorage
   */
  getToken(): string | undefined {
    // Essayer d'abord avec le cookie
    const cookieToken = Cookies.get(TOKEN_COOKIE_NAME);
    if (cookieToken) return cookieToken;
    
    // Fallback sur localStorage
    try {
      const localToken = localStorage.getItem(TOKEN_COOKIE_NAME);
      return localToken || undefined;
    } catch (e) {
      console.warn('Impossible de récupérer le token depuis localStorage', e);
      return undefined;
    }
  },

  /**
   * Supprime le token du cookie et du localStorage
   */
  removeToken(): void {
    try {
      Cookies.remove(TOKEN_COOKIE_NAME);
    } catch (e) {
      console.warn('Impossible de supprimer le token des cookies', e);
    }
    
    try {
      localStorage.removeItem(TOKEN_COOKIE_NAME);
    } catch (e) {
      console.warn('Impossible de supprimer le token du localStorage', e);
    }
  },

  /**
   * Extrait le token de l'URL actuelle (paramètre de requête ou segment de chemin)
   */
  getTokenFromUrl(): string | null {
    // 1. Vérifier d'abord les paramètres de requête (plus fiable en navigation privée)
    const urlParams = new URLSearchParams(window.location.search);
    const queryToken = urlParams.get('token');
    if (validateToken(queryToken)) {
      return queryToken;
    }
    
    // 2. Vérifier ensuite les segments de chemin
    const pathSegments = window.location.pathname.split('/').filter(segment => segment !== '');
    for (const segment of pathSegments) {
      if (validateToken(segment)) {
        return segment;
      }
    }
    
    return null;
  },

  /**
   * Met à jour l'URL avec le token (comme paramètre de requête pour navigation privée)
   */
  updateUrlWithToken(token: string): void {
    // Détecter si on est en navigation privée
    const privateBrowsing = isPrivateBrowsing();
    
    if (privateBrowsing) {
      // En navigation privée, utiliser un paramètre de requête
      const url = new URL(window.location.href);
      url.searchParams.set('token', token);
      window.history.replaceState({}, '', url.toString());
    } else {
      // En navigation normale, utiliser les segments de chemin
      const currentPath = window.location.pathname;
      const pathSegments = currentPath.split('/').filter(segment => segment !== '');
      let tokenFound = false;
      
      for (let i = 0; i < pathSegments.length; i++) {
        if (/^[a-zA-Z0-9]{8}$/.test(pathSegments[i])) {
          pathSegments[i] = token;
          tokenFound = true;
          break;
        }
      }
      
      if (!tokenFound) {
        pathSegments.push(token);
      }

      const newPath = '/' + pathSegments.join('/');
      window.history.replaceState({}, '', newPath);
    }
  },

  /**
   * Synchronise le token entre l'URL et le cookie
   * Retourne le token actif
   */
  synchronizeToken(): string | null {
    const urlToken = this.getTokenFromUrl();
    const cookieToken = this.getToken();


    // Si un token est présent dans l'URL, il a la priorité
    if (urlToken) {
      if (urlToken !== cookieToken) {
        this.setToken(urlToken);
      }
      return urlToken;
    }

    // Si un token est présent dans le cookie, l'injecter dans l'URL
    if (cookieToken) {
      this.updateUrlWithToken(cookieToken);
      return cookieToken;
    }

    return null;
  },

  /**
   * Génère un nouveau token sécurisé
   */
  generateToken(): string {
    return SecurityService.generateSecureToken(8);
  },

  /**
   * Valide un token
   */
  validateToken(token: string): boolean {
    return validateToken(token);
  }
};
